import { Map } from "@cusy/mlgljs-ui";
import maplibregl from "maplibre-gl";
import "./style.css";

function areaColor(opacity) {
  return `rgba(0, 91, 158, ${opacity})`
}

const container = document.getElementById("vdk-map");

if (container) {
  const partnerLabel = container.dataset.partnerLabel || "Partner";

  const showLocations = "showLocations" in container.dataset;

  const stylesheetURL = container.dataset.stylesheet;
  if (stylesheetURL) {
    const styleLink = document.createElement("link");
    styleLink.setAttribute("rel", "stylesheet");
    styleLink.setAttribute("href", stylesheetURL);
    document.head.appendChild(styleLink);
  }

  // Try getting bounds from the data-maxbounds attribute on the container.
  let maxBounds = [[13.0, 52.3], [13.8, 52.7]];  // default: basically Berlin
  try {
    maxBounds = JSON.parse(String(container.dataset.maxbounds));
  } catch {} // ignore, stay at the default then

  // Try getting center from the data-center attribute on the container.
  let center;  // no default, will fall back to center of the bounds
  try {
    center = JSON.parse(String(container.dataset.center));
  } catch {} // ignore, don't use a custom center then

  const map = new Map({
    center,
    container,
    controls: {
      compass: true,
      geocode: true,
      geolocate: true,
      scale: true,
      zoom: true,
    },
    geocodeProfile: "vdk",
    maxBounds,
  });

  map.init().then(() => {
    function hoverArea(event) {
      const id = (event && event.features.length) ? event.features[0].id : "";

      if (id === hoverArea.highlightedID) {
        // Hovered area did not change, do nothing.
        return;
      }

      // If there is already a hovered area, un-hover it.
      if (hoverArea.highlighted) {
        hoverArea.highlighted.forEach(el => mlmap.setFeatureState(el, { hover: false }));
        hoverArea.highlighted = [];
        hoverArea.highlightedID = "";
      }

      if (event) {
        event.features.forEach(el => mlmap.setFeatureState(el, { hover: true }));
        hoverArea.highlighted = event.features;
        hoverArea.highlightedID = id;
      } else {
        hoverArea.highlighted = [];
        hoverArea.highlightedID = "";
      }
    }

    function highlightCenter(id: string) {
      if (id === highlightCenter.highlightedID) {
        // Highlighted center did not change, do nothing.
        return;
      }

      // If there is already a highlighted center, un-highlight it.
      if (highlightCenter.highlighted) {
        highlightCenter.highlighted = [];
        highlightCenter.highlightedID = "";
        highlightCenter.popupHTML = "";
      }

      // Retrieve the center to be highlighted. More than one might be returned
      // due to internals in mlgljs, all of them should be highlighted.
      const found = mlmap.querySourceFeatures("centers", {
        filter: ["==", ["get", "id"], id],
      });
      highlightCenter.highlighted = found;
      highlightCenter.highlightedID = id;

      if (found.length) {
        const props = found[0].properties;
        const address = showLocations ? `
          <tr><th>Adresse</th><td>${props.street} ${props.house}<br />${props.plz} ${props.city}</td></tr>
        ` : "";
        highlightCenter.popupHTML = `
          <tr><th>${partnerLabel}</th><td>${props.name}</td></tr>
          ${address}
        `;
      }
    }

    const mlmap = map.map;
    const hoverPopup = new maplibregl.Popup();

    mlmap.on("style.load", () => {
      const geojsonURL = container.dataset.geojson;
      if (!geojsonURL) {
        return;
      }
      mlmap.addSource("centers", {
        type: "geojson",
        data: geojsonURL,
        promoteId: "id",
      });
      mlmap.addLayer({
        id: "areas",
        type: "fill",
        source: "centers",
        paint: {
          "fill-color": [
            "case",
            ["boolean", ["feature-state", "hover"], false],
            areaColor(0.35),
            areaColor(0.15),
          ],
        },
      });
      mlmap.addLayer({
        id: "points",
        type: "circle",
        source: "centers",
        filter: ["==", "$type", "Point"],
        ...(showLocations ? {
          paint: {
            "circle-color": "#ec1c24",
            "circle-radius": 12,
          },
        } : {
          layout: {
            visibility: "none",
          },
        }),
      });
      mlmap.addLayer({
        id: "station-names",
        type: "symbol",
        source: "pmtiles",
        "source-layer": "pois",
        filter: ["any", ["==", "kind", "station"], ["==", "railway", "station"]],
        layout: {
          "text-font": ["Noto Sans Regular"],
          "text-field": ["get", "name"],
          "text-size": 12,
          "text-offset": [0, 1.2],
        },
        paint: {
          "text-color": "#006",
          "text-halo-width": 1.5,
        },
      });
      mlmap.addLayer({
        id: "stations",
        type: "circle",
        source: "pmtiles",
        "source-layer": "pois",
        filter: ["any", ["==", "kind", "station"], ["==", "railway", "station"]],
        paint: {
          "circle-color": "#006",
          "circle-radius": 4,
        },
      });
      mlmap.on("mousemove", "areas", e => {
        if (!e.features.length) {
          return;
        }
        hoverArea(e);

        // Change the cursor style as a UI indicator.
        mlmap.getCanvas().style.cursor = "pointer";
      });
      mlmap.on("mouseleave", "areas", () => {
        mlmap.getCanvas().style.cursor = "";
        hoverArea();
      });
      mlmap.on("click", "areas", e => {
        if (!e.features.length) {
          return;
        }
        const feature = e.features[0];
        const props = feature.properties;
        highlightCenter(props.center || "");
        hoverArea(e);

        const partnerWithoutCenter = (!props.center && props.partner) ? `
          <tr><th>${partnerLabel}</th><td>${props.partner}</td></tr>
        ` : "";
        const email = props.email ? `
          <tr><th>E-Mail</th><td><a href="mailto:${props.email}">${props.email}</a></td></tr>
        ` : "";
        const phones = props.phone ? `
          <tr><th>Telefon</th><td>${props.phone.split("|").map(phone => `<a href="tel:${phone.replace(/[ ()/.-]/g, "").replace(/^0/, "+49")}">${phone}</a>`).join("<br/>")}</td></tr>
        ` : "";
        const website = props.website ? `
          <tr><th>Website</th><td><a href="${props.website}" target="_blank">${props.website}</a></td></tr>
        ` : "";

        // Change the cursor style as a UI indicator.
        mlmap.getCanvas().style.cursor = "pointer";

        // Populate the popup and set its coordinates
        // based on the feature found.
        hoverPopup.setLngLat(e.lngLat).setHTML(`<table class="vdk-contact">
          <thead><tr><th scope="row" colspan="2">${props.name}</th></tr></thead>
          <tbody>
            ${highlightCenter.popupHTML || partnerWithoutCenter}
            ${website}
            ${email}
            ${phones}
          </tbody>
        </table>`).addTo(mlmap);
      });
    });
  });
}
